import React, { useState, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import PropTypes from 'prop-types';

import Tooltip from '@/components/ui-bridge/core/Tooltip';

import classes from '@/components/OrderToolbar/ActionBox/ActionBox.module.scss';
import ReportOptions from '@/components/ReportOptions/ReportOptions';
import TagModal from '@/components/Tags/TagModal/TagModal';
import repairsIcon from '@/images/icons/repairs.svg';
import { useOrderView } from '@/contexts/OrderViewContext';
import { useUser } from '@/contexts/UserContext';

const SvgIcon = ({ children, style }) => (
  <svg 
    focusable="false" 
    viewBox="0 0 24 24" 
    aria-hidden="true" 
    style={{ ...style, width: '1em', height: '1em', display: 'inline-block', fill: 'currentColor' }}
  >
    {children}
  </svg>
);

const Label = ({ style }) => (
  <SvgIcon style={style}>
    <path d="M17.63 5.84C17.27 5.33 16.67 5 16 5L5 5.01C3.9 5.01 3 5.9 3 7v10c0 1.1.9 1.99 2 1.99L16 19c.67 0 1.27-.33 1.63-.84L22 12l-4.37-6.16z"></path>
  </SvgIcon>
);

const BorderColor = ({ style }) => (
  <SvgIcon style={style}>
    <path d="M17.75 7L14 3.25l-10 10V17h3.75l10-10zm2.96-2.96c.39-.39.39-1.02 0-1.41L18.37.29c-.39-.39-1.02-.39-1.41 0L15 2.25 18.75 6l1.96-1.96z"></path>
    <path fillOpacity=".36" d="M0 20h24v4H0z"></path>
  </SvgIcon>
);

const Input = ({ style }) => (
  <SvgIcon style={style}>
    <path d="M21 3.01H3c-1.1 0-2 .9-2 2V9h2V4.99h18v14.03H3V15H1v4.01c0 1.1.9 1.98 2 1.98h18c1.1 0 2-.88 2-1.98v-14c0-1.11-.9-2-2-2zM11 16l4-4-4-4v3H1v2h10v3z"></path>
  </SvgIcon>
);

const Pdf = ({ style }) => (
  <SvgIcon style={style}>
    <path d="M20 2H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-8.5 7.5c0 .83-.67 1.5-1.5 1.5H9v2H7.5V7H10c.83 0 1.5.67 1.5 1.5v1zm5 2c0 .83-.67 1.5-1.5 1.5h-2.5V7H15c.83 0 1.5.67 1.5 1.5v3zm4-3H19v1h1.5V11H19v2h-1.5V7h3v1.5zM9 9.5h1v-1H9v1zM4 6H2v14c0 1.1.9 2 2 2h14v-2H4V6zm10 5.5h1v-3h-1v3z"></path>
  </SvgIcon>
);

const Delete = ({ style }) => (
  <SvgIcon style={style}>
    <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"></path>
  </SvgIcon>
);

const ActionBox = ({ actionHandler, selectedItem: propSelectedItem, selectedItems, history }) => {
  const intl = useIntl();
  const user = useUser();
  const { activeOrder } = useOrderView();
  
  const [report, setReport] = useState({ open: false, anchor: null });
  const [tagModal, setTagModal] = useState({ visible: false, anchor: null });

  const selectedItem = propSelectedItem || activeOrder;

  const orderTypeConfigurations = useMemo(() => {
    return JSON.parse(window.sessionStorage.getItem('orderTypeConfigurations') || '{}');
  }, []);

  const reportOptionsHandler = event => {
    setReport({ open: true, anchor: event.currentTarget });
  };

  const handleReportListClose = () => {
    setReport({ open: false, anchor: null });
  };

  const openTagModal = evt => {
    setTagModal({ visible: true, anchor: evt.currentTarget });
  };

  const closeTagModal = () => {
    setTagModal({ visible: false, anchor: null });
  };

  const canAddTags = () => selectedItems.size > 0;

  const me = JSON.parse(window.sessionStorage.getItem('me') || '{}');
  const disabledByModality = me.disabledByModality;
  const isPayPerUseModality = me.modality === 'PAY_PER_USE';

  const selectedOrder = selectedItem || {};
  let configurations = {};

  let parentOrderType = selectedOrder.type;
  if (parentOrderType && parentOrderType.startsWith('marketplace_')) {
    parentOrderType = parentOrderType.replace('marketplace_', '');
  }

  let parentOrderStatus = selectedOrder.status;
  if (parentOrderStatus && parentOrderStatus.startsWith('marketplace_')) {
    parentOrderStatus = parentOrderStatus.replace('marketplace_', '');
  }

  if (
    parentOrderType &&
    parentOrderStatus &&
    orderTypeConfigurations &&
    orderTypeConfigurations[parentOrderType] &&
    orderTypeConfigurations[parentOrderType][parentOrderStatus]
  ) {
    configurations = orderTypeConfigurations[parentOrderType][parentOrderStatus];
  }

  const { flags = {}, id: orderId } = selectedOrder;

  return (
    <div className={classes.ActionBoxWrapper}>
      <Tooltip title={<FormattedMessage id="ACTIONS_MENU.TAG" />}>
        <span>
          <button
            type="button"
            disabled={!canAddTags() || disabledByModality}
            onClick={openTagModal}
            data-cy="add-tag-button"
            className={classes.ActionBoxBtn}
          >
            <Label style={{ fontSize: '1.2vw', minWidth: '25px', minHeight: '20px', marginRight: '3px' }} />
            <FormattedMessage id="ACTIONS_MENU.TAG" />
          </button>
        </span>
      </Tooltip>

      <Tooltip title={<FormattedMessage id="ACTIONS_MENU.EDIT" />}>
        <span>
          <button
            type="button"
            disabled={configurations.canBeEdited !== true}
            onClick={e => actionHandler(e, 'edit')}
            data-cy="edit-order-button"
            className={classes.ActionBoxBtn}
          >
            <BorderColor style={{ fontSize: '1.2vw', minWidth: '25px', minHeight: '20px', marginRight: '3px' }} />
            <FormattedMessage id="ACTIONS_MENU.EDIT" />
          </button>
        </span>
      </Tooltip>

      <Tooltip title={<FormattedMessage id="ACTIONS_MENU.EXPORT" />}>
        <span>
          <button
            type="button"
            disabled={configurations.canBeExported !== true || isPayPerUseModality}
            onClick={e => actionHandler(e, 'export')}
            data-cy="export-photos-button"
            className={classes.ActionBoxBtn}
          >
            <Input style={{ fontSize: '1.2vw', minWidth: '25px', minHeight: '20px', marginRight: '3px' }} />
            <FormattedMessage id="ACTIONS_MENU.EXPORT" />
          </button>
        </span>
      </Tooltip>

      <Tooltip title={<FormattedMessage id="ACTIONS_MENU.GENERATE_PDF" />}>
        <span>
          <button
            type="button"
            disabled={configurations.canGenerateReport !== true}
            onClick={reportOptionsHandler}
            data-cy="export-report-button"
            className={classes.ActionBoxBtn}
          >
            <Pdf style={{ fontSize: '1.2vw', minWidth: '25px', minHeight: '20px', marginRight: '3px' }} />
            <FormattedMessage id="ACTIONS_MENU.GENERATE_PDF" />
          </button>
        </span>
      </Tooltip>

      <Tooltip title={<FormattedMessage id="REPAIR_BUDGET" />}>
        <span>
          <button
            type="button"
            disabled={
              configurations.canAccessRepairBudget !== true || flags?.has_damages !== true || disabledByModality
            }
            onClick={() => history.push(`/orders/${orderId}/repair-budget`)}
            className={classes.ActionBoxBtn}
          >
            <img
              src={repairsIcon}
              style={{ fontSize: '1.2vw', minWidth: '25px', minHeight: '20px', marginRight: '3px' }}
              alt={intl.formatMessage({ id: 'REPAIR_BUDGET' })}
            />
            <FormattedMessage id="REPAIR_BUDGET" />
          </button>
        </span>
      </Tooltip>

      <Tooltip title={<FormattedMessage id="ACTIONS_MENU.DELETE" />}>
        <span>
          <button
            type="button"
            disabled={configurations.canBeDeleted !== true}
            onClick={e => actionHandler(e, 'delete')}
            data-cy="delete-order-button"
            className={classes.ActionBoxBtn}
          >
            <Delete style={{ fontSize: '1.2vw', minWidth: '25px', minHeight: '20px', marginRight: '3px' }} />
            <FormattedMessage id="ACTIONS_MENU.DELETE" />
          </button>
        </span>
      </Tooltip>

      <ReportOptions
        show={report.open}
        closeHandler={handleReportListClose}
        anchorEl={report.anchor}
        options={configurations.reportsOptions}
        entityId={selectedOrder.id}
        flags={flags}
      />

      <TagModal
        show={tagModal.visible}
        closeHandler={closeTagModal}
        anchorEl={tagModal.anchor}
        orders={selectedItems}
      />
    </div>
  );
};

ActionBox.propTypes = {
  history: PropTypes.object.isRequired,
  actionHandler: PropTypes.func.isRequired,
  selectedItems: PropTypes.object.isRequired,
  selectedItem: PropTypes.object,
};

export default ActionBox;
